import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Input } from 'rsuite';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';
export default function TissChart({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
    const { t } = useTranslation();
    let cookie = getCookies();

    // Helper to get multilingual value
    const getMultilingualValue = (field: any, lang: LanguageCode, fallback: string = ''): string => {
        if (!field) return fallback;
        if (typeof field === 'string') return field; // Legacy support
        if (typeof field === 'object') {
            return field[lang] || field.en || field.fa || field.ku || field.ar || field.tr || fallback;
        }
        return fallback;
    };

    // Helper to set multilingual value
    const setMultilingualValue = (currentData: any, lang: LanguageCode, value: string): any => {
        if (!currentData || typeof currentData !== 'object') {
            return { [lang]: value };
        }
        return {
            ...currentData,
            [lang]: value
        };
    };

    // Normalize columns for current language
    const normalizeColumnsForLanguage = React.useCallback((cols: any[], lang: LanguageCode): any[] => {
        return cols.map(col => ({
            ...col,
            label: getMultilingualValue(col.label, lang, col.label || '')
        }));
    }, []);

    // Normalize rows for current language
    const normalizeRowsForLanguage = React.useCallback((rows: any[], labelColKey: string, lang: LanguageCode): any[] => {
        return rows.map(row => ({
            ...row,
            [labelColKey]: getMultilingualValue(row[labelColKey], lang, row[labelColKey] || '')
        }));
    }, []);

    // Get current columns and rows for the active language
    const getCurrentColumnsForLang = React.useCallback(() => {
        const cols = data.data?.columns || [];
        return normalizeColumnsForLanguage(cols, activeLang);
    }, [activeLang, JSON.stringify(data?.data?.columns), normalizeColumnsForLanguage]);

    const getCurrentRowsForLang = React.useCallback(() => {
        const rows = data.data?.list || [];
        const labelCol = (data.data?.columns || []).find((col: any) => col.isLabel);
        if (labelCol) {
            return normalizeRowsForLanguage(rows, labelCol.key, activeLang);
        }
        return rows;
    }, [activeLang, JSON.stringify(data?.data?.list), JSON.stringify(data?.data?.columns), normalizeRowsForLanguage]);

    const initialColumns = getCurrentColumnsForLang();
    const initialList = getCurrentRowsForLang();
    const hasContent = initialColumns.length > 0 || initialList.length > 0;

    const [isEditing, setIsEditing] = React.useState(!hasContent);
    const [columns, setColumns] = React.useState<any[]>(initialColumns.length > 0 ? initialColumns : [
        { key: 'name', label: t('Name'), isLabel: true },
        { key: 'value1', label: t('Value 1'), color: COLORS[0] },
        { key: 'value2', label: t('Value 2'), color: COLORS[1] }
    ]);
    const [rows, setRows] = React.useState<any[]>(initialList.length > 0 ? initialList : [
        { name: t('Item 1'), value1: '10', value2: '20' },
        { name: t('Item 2'), value1: '15', value2: '25' },
        { name: t('Item 3'), value1: '20', value2: '30' }
    ]);
    const [saving, setSaving] = React.useState(false);
    const [hasChanges, setHasChanges] = React.useState(false);
    const [editingCell, setEditingCell] = React.useState<{ row: number; col: string } | null>(null);
    const [editingHeader, setEditingHeader] = React.useState<string | null>(null);
    const [editingHeaderValue, setEditingHeaderValue] = React.useState<string>('');
    const [chartType, setChartType] = React.useState<'bar' | 'line' | 'pie'>(data.data?.chartType || 'bar');

    // Update columns and rows when language changes
    React.useEffect(() => {
        const newColumns = getCurrentColumnsForLang();
        const newRows = getCurrentRowsForLang();
        setColumns(newColumns);
        setRows(newRows);
        setHasChanges(false);
    }, [getCurrentColumnsForLang, getCurrentRowsForLang]);

    // Track changes
    React.useEffect(() => {
        const initialChartType = data.data?.chartType || 'bar';
        const currentColumns = getCurrentColumnsForLang();
        const currentRows = getCurrentRowsForLang();
        const dataEqual = JSON.stringify({ columns, rows, chartType }) === JSON.stringify({ columns: currentColumns, rows: currentRows, chartType: initialChartType });
        setHasChanges(!dataEqual);
    }, [columns, rows, chartType, getCurrentColumnsForLang, getCurrentRowsForLang, data.data?.chartType]);

    // Initialize with default chart if empty
    React.useEffect(() => {
        if (!hasContent && isEditing && columns.length === 0) {
            setColumns([
                { key: 'name', label: t('Name'), isLabel: true },
                { key: 'value1', label: t('Value 1'), color: COLORS[0] },
                { key: 'value2', label: t('Value 2'), color: COLORS[1] }
            ]);
            setRows([
                { name: t('Item 1'), value1: '10', value2: '20' },
                { name: t('Item 2'), value1: '15', value2: '25' },
                { name: t('Item 3'), value1: '20', value2: '30' }
            ]);
        }
    }, [hasContent, isEditing]);

    const addColumn = () => {
        const dataColumns = columns.filter(col => !col.isLabel);
        const newKey = `value${dataColumns.length + 1}`;
        const newColumn = { 
            key: newKey, 
            label: `${t('Value')} ${dataColumns.length + 1}`, 
            color: COLORS[dataColumns.length % COLORS.length] 
        };
        setColumns([...columns, newColumn]);
        setRows(rows.map(row => ({ ...row, [newKey]: '' })));
    };

    const addRow = () => {
        const labelCol = columns.find(col => col.isLabel);
        const newRow = columns.reduce((acc, col) => {
            if (col.isLabel) {
                acc[col.key] = `${t('Item')} ${rows.length + 1}`;
            } else {
                acc[col.key] = '';
            }
            return acc;
        }, {} as any);
        setRows([...rows, newRow]);
    };

    const deleteColumn = async (columnKey: string) => {
        const dataColumns = columns.filter(col => !col.isLabel);
        if (dataColumns.length <= 1) {
            Swal.fire({
                title: t('Error'),
                text: t('Chart must have at least one data column'),
                icon: 'warning',
            });
            return;
        }

        const result = await Swal.fire({
            title: t('Delete column?'),
            text: t('This action cannot be undone'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete'),
            cancelButtonText: t('Cancel'),
        });

        if (result.isConfirmed) {
            setColumns(columns.filter(col => col.key !== columnKey));
            setRows(rows.map(row => {
                const newRow = { ...row };
                delete newRow[columnKey];
                return newRow;
            }));
        }
    };

    const deleteRow = async (rowIndex: number) => {
        if (rows.length <= 1) {
            Swal.fire({
                title: t('Error'),
                text: t('Chart must have at least one row'),
                icon: 'warning',
            });
            return;
        }

        const result = await Swal.fire({
            title: t('Delete row?'),
            text: t('This action cannot be undone'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete'),
            cancelButtonText: t('Cancel'),
        });

        if (result.isConfirmed) {
            setRows(rows.filter((_, i) => i !== rowIndex));
        }
    };

    const updateCell = (rowIndex: number, columnKey: string, value: string) => {
        const labelCol = columns.find(col => col.isLabel);
        const updatedRows = rows.map((row, idx) => {
            if (idx === rowIndex) {
                const newRow = { ...row };
                // If this is the label column, update multilingual value
                if (labelCol && columnKey === labelCol.key) {
                    newRow[columnKey] = setMultilingualValue(row[columnKey], activeLang, value);
                } else {
                    newRow[columnKey] = value;
                }
                return newRow;
            }
            return row;
        });
        setRows(updatedRows);
    };

    const updateHeader = (columnKey: string, newLabel: string) => {
        if (!newLabel.trim()) {
            Swal.fire({
                title: t('Error'),
                text: t('Column name cannot be empty'),
                icon: 'warning',
            });
            return;
        }
        setColumns(columns.map(col => {
            if (col.key === columnKey) {
                // Update multilingual label
                const updatedLabel = setMultilingualValue(col.label, activeLang, newLabel.trim());
                return { ...col, label: updatedLabel };
            }
            return col;
        }));
        setEditingHeader(null);
        setEditingHeaderValue('');
    };

    const updateColumnColor = (columnKey: string, color: string) => {
        setColumns(columns.map(col => 
            col.key === columnKey ? { ...col, color } : col
        ));
    };

    const save = async () => {
        if (!data?._id) return;

        setSaving(true);
        try {
            // Convert columns and rows to multilingual format before saving
            const multilingualColumns = columns.map(col => ({
                ...col,
                label: setMultilingualValue(col.label, activeLang, col.label)
            }));
            
            const labelCol = columns.find(col => col.isLabel);
            const multilingualRows = rows.map(row => {
                const newRow = { ...row };
                if (labelCol) {
                    newRow[labelCol.key] = setMultilingualValue(row[labelCol.key], activeLang, row[labelCol.key] || '');
                }
                return newRow;
            });

            await axios.put(
                `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
                { list: multilingualRows, columns: multilingualColumns, chartType },
                {
                    headers: {
                        Authorization: `Bearer ${cookie['token']}`,
                    },
                }
            );

            Swal.fire({
                title: t('Saved successfully'),
                icon: 'success',
                timer: 1200,
                showConfirmButton: false,
            });
            setIsEditing(false);
            setHasChanges(false);
            if (reload) reload();
        } catch (error: any) {
            console.error('Error saving data:', error);
            Swal.fire({
                title: t('Error saving'),
                icon: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges) {
            Swal.fire({
                title: t('Discard changes?'),
                text: t('You have unsaved changes. Are you sure you want to cancel?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('Yes, discard'),
                cancelButtonText: t('No, keep editing'),
            }).then((result) => {
                if (result.isConfirmed) {
                    const currentColumns = getCurrentColumnsForLang();
                    const currentRows = getCurrentRowsForLang();
                    setColumns(currentColumns.length > 0 ? currentColumns : [
                        { key: 'name', label: t('Name'), isLabel: true },
                        { key: 'value1', label: t('Value 1'), color: COLORS[0] },
                        { key: 'value2', label: t('Value 2'), color: COLORS[1] }
                    ]);
                    setRows(currentRows.length > 0 ? currentRows : [
                        { name: t('Item 1'), value1: '10', value2: '20' },
                        { name: t('Item 2'), value1: '15', value2: '25' },
                        { name: t('Item 3'), value1: '20', value2: '30' }
                    ]);
                    setChartType(data.data?.chartType || 'bar');
                    setIsEditing(false);
                    setHasChanges(false);
                }
            });
        } else {
            setIsEditing(false);
        }
    };

    // Prepare chart data
    const chartData = rows.map((row) => {
        const dataPoint: any = {};
        columns.forEach((col) => {
            if (col.isLabel) {
                dataPoint.name = row[col.key] || '';
            } else {
                const numValue = parseFloat(row[col.key] || '0');
                dataPoint[col.label] = isNaN(numValue) ? 0 : numValue;
            }
        });
        return dataPoint;
    });

    const dataColumns = columns.filter(col => !col.isLabel);
    const labelColumn = columns.find(col => col.isLabel);

    // Empty state
    if (!hasContent && !isEditing) {
        return (
            <div
                className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
                onClick={() => setIsEditing(true)}
                dir="rtl"
            >
                <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                </div>
                <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
                    {t('Click to add chart')}
                </span>
                <span className="text-gray-400 text-xs mt-1">
                    {t('Create a data visualization chart')}
                </span>
            </div>
        );
    }

    // Preview Mode
    if (!isEditing) {
        return (
            <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
                <div className="p-0.5 relative">
                    {rows.length > 0 && dataColumns.length > 0 ? (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <ResponsiveContainer width="100%" height={350}>
                                {chartType === 'bar' ? (
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="name" stroke="#6b7280" />
                                        <YAxis stroke="#6b7280" />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'white', 
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px'
                                            }} 
                                        />
                                        <Legend />
                                        {dataColumns.map((col, index) => (
                                            <Bar
                                                key={col.key}
                                                dataKey={col.label}
                                                fill={col.color || COLORS[index % COLORS.length]}
                                                radius={[8, 8, 0, 0]}
                                            />
                                        ))}
                                    </BarChart>
                                ) : chartType === 'line' ? (
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="name" stroke="#6b7280" />
                                        <YAxis stroke="#6b7280" />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'white', 
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px'
                                            }} 
                                        />
                                        <Legend />
                                        {dataColumns.map((col, index) => (
                                            <Line
                                                key={col.key}
                                                type="monotone"
                                                dataKey={col.label}
                                                stroke={col.color || COLORS[index % COLORS.length]}
                                                strokeWidth={2}
                                                dot={{ r: 5 }}
                                            />
                                        ))}
                                    </LineChart>
                                ) : (
                                    <PieChart>
                                        <Pie
                                            data={chartData}
                                            dataKey={dataColumns[0]?.label || 'value'}
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            label
                                        >
                                            {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={dataColumns[0]?.color || COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'white', 
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px'
                                            }} 
                                        />
                                        <Legend />
                                    </PieChart>
                                )}
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-400">
                            <div className="flex flex-col items-center">
                                <svg className="w-12 h-12 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                <p className="text-sm">{t('No chart data')}</p>
                            </div>
                        </div>
                    )}

                    {/* Edit Button - Appears on hover */}
                    {rows.length > 0 && dataColumns.length > 0 && (
                        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t('Edit Chart')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
            <div className="p-0.5">
                <div className="space-y-4">
                    {/* Chart Type Selector */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border-2 border-blue-100 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <label className="block text-base font-bold text-gray-800 text-right">
                                {t('Chart Type')}
                            </label>
                            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-xs text-gray-600 font-medium">{t('Live Preview')}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            {(['bar', 'line', 'pie'] as const).map((type) => (
                                <button
                                    key={type}
                                    onClick={() => setChartType(type)}
                                    className={`relative px-4 py-4 rounded-xl text-sm font-semibold transition-all duration-300 flex flex-col items-center gap-3 ${
                                        chartType === type
                                            ? 'bg-blue-600 text-white shadow-xl scale-105 ring-4 ring-blue-200'
                                            : 'bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-400 border-2 border-gray-200 hover:shadow-lg'
                                    }`}
                                >
                                    {chartType === type && (
                                        <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1.5 shadow-lg">
                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    )}
                                    {type === 'bar' && (
                                        <>
                                            <svg className={`w-8 h-8 ${chartType === type ? 'text-white' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                            <span>{t('Bar Chart')}</span>
                                        </>
                                    )}
                                    {type === 'line' && (
                                        <>
                                            <svg className={`w-8 h-8 ${chartType === type ? 'text-white' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                            </svg>
                                            <span>{t('Line Chart')}</span>
                                        </>
                                    )}
                                    {type === 'pie' && (
                                        <>
                                            <svg className={`w-8 h-8 ${chartType === type ? 'text-white' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H11V2.05a9.001 9.001 0 009.488 6.95z" />
                                            </svg>
                                            <span>{t('Pie Chart')}</span>
                                        </>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-3 flex-wrap">
                        <button
                            onClick={addColumn}
                            className="px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-2 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                            <div className="bg-white/20 rounded-lg p-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                </svg>
                            </div>
                            {t('Add Column')}
                        </button>
                        <button
                            onClick={addRow}
                            className="px-5 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center gap-2 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                            <div className="bg-white/20 rounded-lg p-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                </svg>
                            </div>
                            {t('Add Row')}
                        </button>
                    </div>

                    {/* Chart Preview */}
                    {rows.length > 0 && dataColumns.length > 0 ? (
                        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border-2 border-blue-200 shadow-xl">
                            <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="bg-blue-100 rounded-lg p-2">
                                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-gray-800 text-right">{t('Live Preview')}</h3>
                                        <p className="text-xs text-gray-500 mt-0.5">{t('Changes update instantly')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-full border border-green-200">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                    <span className="text-xs text-green-700 font-semibold">{t('Live')}</span>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-inner">
                                <ResponsiveContainer width="100%" height={380}>
                                {chartType === 'bar' ? (
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="name" stroke="#6b7280" />
                                        <YAxis stroke="#6b7280" />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'white', 
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px'
                                            }} 
                                        />
                                        <Legend />
                                        {dataColumns.map((col, index) => (
                                            <Bar
                                                key={col.key}
                                                dataKey={col.label}
                                                fill={col.color || COLORS[index % COLORS.length]}
                                                radius={[8, 8, 0, 0]}
                                            />
                                        ))}
                                    </BarChart>
                                ) : chartType === 'line' ? (
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="name" stroke="#6b7280" />
                                        <YAxis stroke="#6b7280" />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'white', 
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px'
                                            }} 
                                        />
                                        <Legend />
                                        {dataColumns.map((col, index) => (
                                            <Line
                                                key={col.key}
                                                type="monotone"
                                                dataKey={col.label}
                                                stroke={col.color || COLORS[index % COLORS.length]}
                                                strokeWidth={2}
                                                dot={{ r: 5 }}
                                            />
                                        ))}
                                    </LineChart>
                                ) : (
                                    <PieChart>
                                        <Pie
                                            data={chartData}
                                            dataKey={dataColumns[0]?.label || 'value'}
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            label
                                        >
                                            {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={dataColumns[0]?.color || COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'white', 
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px'
                                            }} 
                                        />
                                        <Legend />
                                    </PieChart>
                                )}
                                </ResponsiveContainer>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-12 border-2 border-dashed border-blue-200 text-center">
                            <div className="bg-blue-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <p className="text-gray-700 font-semibold mb-2 text-base">{t('Add data to see chart preview')}</p>
                            <p className="text-sm text-gray-500 max-w-md mx-auto">{t('Add columns and rows above to visualize your data')}</p>
                        </div>
                    )}

                    {/* Data Table Editor */}
                    <div className="bg-white rounded-xl p-5 border-2 border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <div className="bg-purple-100 rounded-lg p-2">
                                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-gray-800 text-right">{t('Chart Data')}</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">{t('Click cells to edit values')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-x-auto border-2 border-gray-200 rounded-xl bg-white shadow-inner">
                            <table className="w-full border-collapse bg-white">
                            <thead>
                                <tr className="bg-gray-50 border-b-2 border-gray-300">
                                    {columns.map((col) => (
                                        <th
                                            key={col.key}
                                            className="border border-gray-200 px-3 py-3 text-right min-w-[150px] relative group/header"
                                        >
                                            {editingHeader === col.key ? (
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        value={editingHeaderValue}
                                                        onChange={(value) => setEditingHeaderValue(value || '')}
                                                        onPressEnter={() => updateHeader(col.key, editingHeaderValue)}
                                                        className="flex-1"
                                                        style={{
                                                            padding: '6px 10px',
                                                            border: '1px solid #3b82f6',
                                                            borderRadius: '6px',
                                                            textAlign: 'right',
                                                        }}
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() => updateHeader(col.key, editingHeaderValue)}
                                                        className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEditingHeader(null);
                                                            setEditingHeaderValue('');
                                                        }}
                                                        className="p-1.5 bg-gray-400 text-white rounded hover:bg-gray-500 transition-colors"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                    <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2 flex-1">
                                                        {!col.isLabel && (
                                                            <div className="relative group/color">
                                                                <div className="relative">
                                                                    <input
                                                                        type="color"
                                                                        value={col.color || COLORS[0]}
                                                                        onChange={(e) => updateColumnColor(col.key, e.target.value)}
                                                                        className="w-10 h-10 rounded-lg border-2 border-gray-300 cursor-pointer hover:border-blue-500 hover:scale-110 transition-all shadow-md hover:shadow-lg"
                                                                        title={t('Change color')}
                                                                    />
                                                                    <div className="absolute inset-0 rounded-lg ring-2 ring-offset-2 ring-blue-400 opacity-0 group-hover/color:opacity-100 transition-opacity pointer-events-none"></div>
                                                                </div>
                                                                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 opacity-0 group-hover/color:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap pointer-events-none shadow-lg z-20">
                                                                    {t('Change color')}
                                                                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                                                        <div className="border-4 border-transparent border-t-gray-900"></div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {col.isLabel && (
                                                            <div className="px-3 py-1.5 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 rounded-lg text-xs font-bold shadow-sm border border-blue-200">
                                                                {t('Label')}
                                                            </div>
                                                        )}
                                                        <span
                                                            className="flex-1 cursor-pointer hover:text-blue-600 transition-colors font-semibold text-gray-700"
                                                            onClick={() => {
                                                                setEditingHeader(col.key);
                                                                setEditingHeaderValue(col.label);
                                                            }}
                                                        >
                                                            {col.label}
                                                        </span>
                                                    </div>
                                                    {!col.isLabel && dataColumns.length > 1 && (
                                                        <button
                                                            onClick={() => deleteColumn(col.key)}
                                                            className="opacity-0 group-hover/header:opacity-100 p-1.5 hover:bg-red-100 text-red-600 rounded transition-all"
                                                            title={t('Delete column')}
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                    <th className="border border-gray-200 px-3 py-3 bg-gray-50 w-16"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, rowIndex) => (
                                    <tr key={rowIndex} className="border-b border-gray-200 hover:bg-gray-50 transition-colors group/row">
                                        {columns.map((col) => (
                                            <td
                                                key={col.key}
                                                className="border border-gray-200 px-3 py-2"
                                            >
                                                {editingCell?.row === rowIndex && editingCell?.col === col.key ? (
                                                    <Input
                                                        value={row[col.key] || ''}
                                                        onChange={(value) => updateCell(rowIndex, col.key, value || '')}
                                                        onBlur={() => setEditingCell(null)}
                                                        onPressEnter={() => setEditingCell(null)}
                                                        className="w-full"
                                                        style={{
                                                            padding: '6px 10px',
                                                            border: '1px solid #3b82f6',
                                                            borderRadius: '6px',
                                                            textAlign: 'right',
                                                        }}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <div
                                                        className="px-3 py-2 min-h-[36px] cursor-pointer hover:bg-blue-50 rounded transition-colors text-gray-700"
                                                        onClick={() => setEditingCell({ row: rowIndex, col: col.key })}
                                                    >
                                                        {row[col.key] || <span className="text-gray-400 italic">{t('Click to edit')}</span>}
                                                    </div>
                                                )}
                                            </td>
                                        ))}
                                        <td className="border border-gray-200 px-2 py-2 bg-gray-50">
                                            {rows.length > 1 && (
                                                <button
                                                    onClick={() => deleteRow(rowIndex)}
                                                    className="opacity-0 group-hover/row:opacity-100 p-1.5 hover:bg-red-100 text-red-600 rounded transition-all"
                                                    title={t('Delete row')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex gap-3 justify-end items-center">
                    {hasChanges && (
                        <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
                            {t('Unsaved changes')}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={saving}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {t('Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !hasChanges}
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
                    >
                        {saving ? (
                            <>
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>{t('Saving...')}</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>{t('Save')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
